import React, { useState, useMemo, useEffect } from 'react';
import { CellProps } from 'react-table';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import Spacer from '~/components/Spacer/Spacer';
import CobuConstants from '~/helpers/cobuConstants';
import GenericModal from '~/components/GenericModal/GenericModal';
import MultiSelect from '~/components/MultiSelect/MultiSelect';
import { SelectType } from '~/enums/SelectType';
import useManageWords from '~/hooks/managewords/useManageWords';
import { ModeratedWord, WordLanguage } from '~/types/manageWords';
import ManageWordForm from './ManageWordsForm';
import styles from './ManageWordsList.module.css';

interface ManageWordsListProps {
  manageWords: ReturnType<typeof useManageWords>;
}

export type WordCategory = 'forbidden' | 'moderated' | 'blocked' | 'alwaysHide';

type CurrentWord = ModeratedWord & {
  type: WordCategory;
};

const GLOBAL_SCOPE = 'global';

const WORD_CATEGORY_LABELS: Record<WordCategory, {
  table: string;
  create: string;
  update: string;
}> = {
  forbidden: {
    table: CobuConstants.manageWords.forbiddenWords,
    create: CobuConstants.manageWords.createForbiddenWord,
    update: CobuConstants.manageWords.updateForbiddenWord,
  },
  moderated: {
    table: CobuConstants.manageWords.moderatedWords,
    create: CobuConstants.manageWords.createModeratedWord,
    update: CobuConstants.manageWords.updateModeratedWord,
  },
  blocked: {
    table: CobuConstants.manageWords.blockedWords,
    create: CobuConstants.manageWords.createBlockedWord,
    update: CobuConstants.manageWords.updateBlockedWord,
  },
  alwaysHide: {
    table: CobuConstants.manageWords.alwaysHideWords,
    create: CobuConstants.manageWords.createAlwaysHideWord,
    update: CobuConstants.manageWords.updateAlwaysHideWord,
  },
};

export default function ManageWordsList({ manageWords }: ManageWordsListProps) {
  const {
    forbiddenWords,
    moderatedWords,
    blockedWords,
    alwaysHideWords,
    buildingOptions,
    selectedBuildingId,
    setSelectedBuildingId,
    createWord,
    updateWord,
    deleteWord
  } = manageWords;
  const [currentWord, setCurrentWord] = useState<CurrentWord | null>(null);
  const [createModalType, setCreateModalType] = useState<WordCategory | null>(
    null
  );

  const buildingOptionsWithGlobal = useMemo(
    () => [
      { value: GLOBAL_SCOPE, label: CobuConstants.manageWords.buildingGlobalLabel },
      ...buildingOptions.map(building => ({ value: building.id, label: building.name }))
    ],
    [buildingOptions]
  );

  const selectedBuildingOption = useMemo(
    () => buildingOptionsWithGlobal.find(option => option.value === selectedBuildingId) || null,
    [buildingOptionsWithGlobal, selectedBuildingId]
  );

  const [selectedBuildingValue, setSelectedBuildingValue] = useState<{ value: string; label: string }[]>(
    selectedBuildingOption ? [selectedBuildingOption] : []
  );

  const tableConfig = useMemo(() => ({
    forbidden: getTableConfig('forbidden'),
    moderated: getTableConfig('moderated'),
    blocked: getTableConfig('blocked'),
    alwaysHide: getTableConfig('alwaysHide')
  }), []);

  useEffect(() => {
    if (selectedBuildingOption) {
      setSelectedBuildingValue([selectedBuildingOption]);
    } else {
      setSelectedBuildingValue([]);
    }
  }, [selectedBuildingOption]);

  return (
    <div className='content-container'>
      <Breadcrumbs />
      <Spacer height={20} />
      <h1>{CobuConstants.manageWords.title}</h1>
      <Spacer height={20} />
      <div className={styles.filterRow}>
        <label htmlFor='buildingFilter' className={styles.filterLabel}>
          {CobuConstants.manageWords.buildingFilterLabel}
        </label>
        <div style={{ width: '300px' }}>
          <MultiSelect
            options={buildingOptionsWithGlobal}
            placeholder="Search buildings..."
            isSearchable={true}
            selectType={SelectType.Form}
            disabled={false}
            isClearable={false}
            noOptionsMessage="No buildings found"
            isError={null}
            multiValue={selectedBuildingValue}
            setMultiValue={(newValue: { value: string; label: string }[]) => {
              setSelectedBuildingValue(newValue);
              if (newValue.length > 0) {
                setSelectedBuildingId(newValue[0].value);
              } else {
                setSelectedBuildingId(GLOBAL_SCOPE);
              }
            }}
            cobuPlusBuildings={[]}
            singleSelect={true}
          />
        </div>
      </div>
      <Spacer height={20} />
      {getManageWordsTables()}
      {getCreateWordModal()}
      {getEditWordModal()}
    </div>
  );

  function getManageWordsTables() {
    return (
      <div className={styles.container}>
        <div className={styles.tableContainer}>
          {getCobuTable(forbiddenWords, 'forbidden')}
        </div>
        <div className={styles.tableContainer}>
          {getCobuTable(moderatedWords, 'moderated')}
        </div>
        <div className={styles.tableContainer}>
          {getCobuTable(blockedWords, 'blocked')}
        </div>
        <div className={styles.tableContainer}>
          {getCobuTable(alwaysHideWords, 'alwaysHide')}
        </div>
      </div>
    );
  }

  function getCobuTable(data: ModeratedWord[], type: WordCategory) {
    const { table: title, create: action } = WORD_CATEGORY_LABELS[type];
    return (
      <div>
        <div className={styles.tableHeader}>
          <h3>{title}</h3>
          <div className='margin-left-16'>
            <button
              className='btn-primary btn-standard bold'
              onClick={() => setCreateModalType(type)}
              aria-label={`Create new ${type} word`}
            >
              {action}
            </button>
          </div>
        </div>
        <CobuTableAdvanced
          columns={tableConfig[type]}
          data={data}
          noData={{ label: action }}
          actionsStyle={{ top: '130px' }}
        />
      </div>
    );
  }

  function getTableConfig(type: WordCategory) {
    return [
      {
        Header: 'TERM',
        accessor: 'term',
        Cell: (cell: CellProps<ModeratedWord>) => (
          <div className={`${styles.tableContent} default-text`}>
            {cell.row.original.term}
          </div>
        )
      },
      {
        Header: 'LANGUAGE',
        accessor: 'language',
        Cell: (cell: CellProps<ModeratedWord>) => (
          <div className={`${styles.tableContent} default-text`}>
            {cell.row.original.language === 'es' ? 'Spanish' : 'English'}
          </div>
        )
      },
      {
        id: 'delete',
        Cell: (cell: CellProps<ModeratedWord>) => (
          <div
            className={'more-actions-btn more-actions-btn-unselected pointer'}
            onClick={() =>
              deleteWord(type, cell.row.original.uuid, cell.row.original.building)
            }
            role="button"
            tabIndex={0}
            aria-label={`Delete ${type} word: ${cell.row.original.term}`}
          >
            {CobuConstants.manageWords.delete}
          </div>
        )
      },
      {
        id: 'edit',
        Cell: (cell: CellProps<ModeratedWord>) => (
          <div
            className={'more-actions-btn more-actions-btn-unselected pointer'}
            onClick={() => {
              setCurrentWord({
                type,
                ...cell.row.original
              });
            }}
            role="button"
            tabIndex={0}
            aria-label={`Edit ${type} word: ${cell.row.original.term}`}
          >
            {CobuConstants.manageWords.edit}
          </div>
        )
      }
    ];
  }

  function getEditWordModal() {
    if (!currentWord) return null;

    return (
      <GenericModal
        showModal={true}
        title={getTitle()}
        content={
          <ManageWordForm
            isUpdate={true}
            onPressCancel={onPressCancel}
            term={currentWord.term}
            onSubmit={onSubmit}
            buildingOptions={buildingOptionsWithGlobal}
            initialBuildingId={currentWord.building || GLOBAL_SCOPE}
            initialLanguage={currentWord.language || 'en'}
          />
        }
        hideActionButton
        canceActionButton
        actionText={''}
        actionFunction={() => {}}
        onChange={() => {}}
      />
    );

    function onPressCancel() {
      setCurrentWord(null);
    }
    
    function getTitle() {
      return currentWord ? WORD_CATEGORY_LABELS[currentWord.type].update : '';
    }

    async function onSubmit(
      term: string,
      buildingId?: string | null,
      language?: WordLanguage
    ) {
      await updateWord(
        term,
        currentWord?.type as WordCategory,
        currentWord?.uuid as string,
        buildingId === GLOBAL_SCOPE ? null : buildingId,
        language
      );

      setCurrentWord(null);
    }
  }

  function getCreateWordModal() {
    if (!createModalType) return null;

    return (
      <GenericModal
        showModal={true}
        title={getTitle()}
        content={
          <ManageWordForm
            onPressCancel={onPressCancel}
            onSubmit={onSubmit}
            buildingOptions={buildingOptionsWithGlobal}
            initialBuildingId={selectedBuildingId}
          />
        }
        hideActionButton
        canceActionButton
        actionText={''}
        actionFunction={() => {}}
        onChange={() => {}}
      />
    );

    function onPressCancel() {
      setCreateModalType(null);
    }

    async function onSubmit(
      term: string,
      buildingId?: string | null,
      language?: WordLanguage
    ) {
      await createWord(
        term,
        createModalType as WordCategory,
        buildingId === GLOBAL_SCOPE ? null : buildingId,
        language
      );
      setCreateModalType(null);
    }

    function getTitle() {
      return createModalType ? WORD_CATEGORY_LABELS[createModalType].create : '';
    }
  }
}
